import React, { useEffect, useState, useCallback } from 'react';
import {
  AlertTriangle, ThumbsUp, ThumbsDown, ExternalLink,
  Send, CornerUpRight, PauseCircle, Filter, PencilLine, Search,
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import AppShell from '../components/AppShell';

const GORGIAS = 'https://lamptwist.gorgias.com/app/ticket/';
const pct = (v) => (v == null ? '-' : `${Math.round(v * 100)}%`);
const ago = (iso) => {
  if (!iso) return '-';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  return `${Math.round(m / 60)}h ago`;
};

// single-hue magnitude palette (ink stays on text tokens, color on marks)
const INK = '#059669';        // emerald-600 - the pipeline's "go" hue
const WARN = '#b45309';       // amber-700
const CRIT = '#b91c1c';       // red-700
const TRACK = 'hsl(var(--secondary))';

const HEALTH = {
  live: { label: 'Live', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  stale: { label: 'Stale', cls: 'bg-amber-50 text-amber-800 border-amber-200', dot: 'bg-amber-500' },
  idle: { label: 'Idle', cls: 'bg-stone-100 text-stone-600 border-stone-200', dot: 'bg-stone-400' },
};

// What replai DID on a run → icon, colors, verb. `tone` comes from the API.
const ACTIONS = {
  posted:    { icon: Send,         rail: '#059669', chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', verb: 'Posted' },
  escalated: { icon: CornerUpRight,rail: '#0e7490', chip: 'bg-cyan-50 text-cyan-700 ring-cyan-200',        verb: 'Escalated' },
  held:      { icon: PauseCircle,  rail: '#b45309', chip: 'bg-amber-50 text-amber-800 ring-amber-200',     verb: 'Held back' },
  noise:     { icon: Filter,       rail: '#9ca3af', chip: 'bg-stone-100 text-stone-600 ring-stone-200',    verb: 'Filtered' },
  failed:    { icon: AlertTriangle,rail: '#b91c1c', chip: 'bg-red-50 text-red-700 ring-red-200',           verb: 'Errored' },
  drafted:   { icon: PencilLine,   rail: '#6366f1', chip: 'bg-indigo-50 text-indigo-700 ring-indigo-200',  verb: 'Drafted' },
};

// Turn a raw endpoint call into a short human chip label.
function dataLabel({ name, detail }) {
  const n = (name || '').toLowerCase();
  if (n.includes('order_by_number') || (n.includes('order') && detail)) return `order #${detail}`;
  if (n.includes('refund')) return 'refund status';
  if (n.includes('track')) return 'tracking';
  if (n.includes('availability') || n.includes('stock')) return 'stock';
  if (n.includes('specs')) return 'specs';
  if (n.includes('invoice')) return 'invoices';
  if (n.includes('products/search') || n.includes('search')) return 'product search';
  if (n.includes('products/{id}') || n.includes('product')) return 'product';
  if (n.includes('orders/by-email') || n.includes('orders')) return 'orders';
  return (name || '').replace(/GET |POST |\(triage-context\)|[{}]/g, '').replace(/\/+/g, ' ').trim().slice(0, 22) || 'lookup';
}

function DataChip({ call }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[0.65rem] font-medium ring-1 ring-inset ${
      call.ok ? 'bg-slate-50 text-slate-600 ring-slate-200' : 'bg-stone-50 text-stone-400 ring-stone-200 line-through'
    }`} title={call.name}>
      <Search className="h-2.5 w-2.5" />{dataLabel(call)}
    </span>
  );
}

function ActivityRow({ r }) {
  const a = ACTIONS[r.action] || ACTIONS.drafted;
  const Icon = a.icon;
  return (
    <div className="group relative flex gap-3 pl-4 pr-3 py-3 hover:bg-[hsl(var(--secondary))] transition-colors">
      <span className="absolute left-0 top-0 bottom-0 w-1 rounded-r" style={{ background: a.rail }} />
      <div className="mt-0.5 shrink-0 grid place-items-center h-8 w-8 rounded-full ring-1 ring-inset" style={{ color: a.rail, background: `${a.rail}14`, borderColor: `${a.rail}33` }}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[0.65rem] font-semibold ring-1 ring-inset ${a.chip}`}>
            {a.verb}{r.action === 'escalated' && r.agent ? ` → ${r.agent}` : ''}
          </span>
          <span className="text-xs text-muted-foreground truncate">{(r.category || 'unknown').replace(/_/g, ' ')}</span>
          {r.confidence != null && (
            <span className={`ml-auto shrink-0 text-[0.65rem] font-semibold tabular-nums px-1.5 py-0.5 rounded-md ${
              r.confidence >= 0.8 ? 'bg-emerald-50 text-emerald-700' : r.confidence >= 0.6 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
            }`}>{pct(r.confidence)}</span>
          )}
        </div>
        <p className="mt-1 text-sm text-foreground truncate">{r.subject || `Ticket ${r.gorgias_id}`}</p>
        {(r.data_calls?.length > 0 || r.reason) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {r.data_calls?.map((c, i) => <DataChip key={i} call={c} />)}
            {r.reason && r.data_calls?.length === 0 && (
              <span className="text-[0.7rem] text-muted-foreground italic truncate">{r.reason}</span>
            )}
          </div>
        )}
        <div className="mt-1.5 flex items-center gap-3 text-[0.65rem] text-muted-foreground">
          <span>{ago(r.created_at)}</span>
          {r.model && <span className="opacity-70">· {r.model === 'standard' ? 'Sonnet' : r.model === 'simple' ? 'Haiku' : r.model}</span>}
          {r.provider && (
            <span className={`inline-flex items-center gap-1 rounded px-1 py-0.5 text-[0.6rem] font-medium ${
              r.provider === 'ferry' ? 'bg-violet-50 text-violet-700' : 'bg-amber-50 text-amber-700'
            }`} title={r.provider === 'ferry' ? 'Served by the Ferry gateway (flat pack)' : 'Served by the Anthropic API (metered)'}>
              {r.provider === 'ferry' ? 'Ferry' : 'API'}
            </span>
          )}
          {r.feedback === 'correct' && <span className="inline-flex items-center gap-0.5 text-emerald-600"><ThumbsUp className="h-3 w-3" /> good</span>}
          {r.feedback === 'incorrect' && <span className="inline-flex items-center gap-0.5 text-red-600"><ThumbsDown className="h-3 w-3" /> off</span>}
          <a href={`${GORGIAS}${r.gorgias_id}`} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 hover:text-foreground transition-opacity">
            open <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  );
}

// Horizontal magnitude bar row
function Bar({ label, value, max, color = INK, hint }) {
  const w = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1" title={hint}>
      <span className="w-32 shrink-0 text-xs text-muted-foreground truncate">{label}</span>
      <div className="flex-1 h-5 rounded-md" style={{ background: TRACK }}>
        <div className="h-5 rounded-md transition-all" style={{ width: `${w}%`, background: color, minWidth: value ? 8 : 0 }} />
      </div>
      <span className="w-8 shrink-0 text-right text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}

// Confidence / rate meter (0..1)
function Meter({ label, value }) {
  const w = value == null ? 0 : Math.round(value * 100);
  const color = value == null ? TRACK : value >= 0.8 ? INK : value >= 0.6 ? WARN : CRIT;
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        <span className="text-xl font-semibold tabular-nums">{value == null ? '-' : `${w}%`}</span>
      </div>
      <div className="h-2 rounded-full" style={{ background: TRACK }}>
        <div className="h-2 rounded-full" style={{ width: `${w}%`, background: color }} />
      </div>
    </div>
  );
}

// 7-day column trend (drafted underlay, posted overlay)
function Trend({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.drafted));
  return (
    <div className="rounded-xl border border-border p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">Last 7 days - drafted vs posted</p>
      <div className="flex items-end justify-between gap-2 h-28">
        {daily.map((d) => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.drafted} drafted, ${d.posted} posted`}>
            <div className="relative w-full flex justify-center" style={{ height: 88 }}>
              <div className="absolute bottom-0 w-5 rounded-t" style={{ height: `${(d.drafted / max) * 88}px`, background: TRACK }} />
              <div className="absolute bottom-0 w-5 rounded-t" style={{ height: `${(d.posted / max) * 88}px`, background: INK }} />
            </div>
            <span className="text-[0.6rem] text-muted-foreground">{d.date.slice(5)}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-4 mt-2 text-[0.65rem] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: INK }} /> posted</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: TRACK }} /> drafted</span>
      </div>
    </div>
  );
}

export default function Overview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('api/replai/overview/');
      setD(data); setErr(false);
    } catch { setErr(true); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const h = d?.health;
  const hs = HEALTH[h?.status] || HEALTH.idle;
  const t = d?.today;
  const funnelMax = t ? Math.max(t.ingested, t.drafted, t.posted, 1) : 1;
  const ad = d?.week?.adoption;
  const adTotal = ad ? ad.verbatim + ad.adapted + ad.ignored : 0;

  return (
    <AppShell title="Overview" subtitle="Your pilot view of the live pipeline - health, throughput, and quality at a glance.">
      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">
          <AlertTriangle className="h-4 w-4" /> Couldn't load the overview.
          <button onClick={load} className="ml-auto underline">Retry</button>
        </div>
      )}
      {!d ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-5">
          {/* Health banner */}
          <div className={`flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border px-4 py-3 ${hs.cls}`}>
            <div className="flex items-center gap-2 font-semibold">
              <span className={`h-2.5 w-2.5 rounded-full ${hs.dot} ${h?.status === 'live' ? 'animate-pulse' : ''}`} />
              Pipeline {hs.label}
            </div>
            <div className="text-sm">Last draft <b>{ago(h?.latest_run_at)}</b></div>
            <div className="text-sm">Last ingest <b>{ago(h?.last_ingest_at)}</b></div>
            <div className="text-sm ml-auto">Auto-post <b>{h?.auto_note_enabled ? 'ON' : 'OFF'}</b></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Today funnel */}
            <section className="rounded-xl border border-border p-4">
              <h2 className="text-sm font-semibold mb-3">Today's flow</h2>
              <Bar label="Ingested" value={t.ingested} max={funnelMax} color="#6b7280" />
              <Bar label="Drafted" value={t.drafted} max={funnelMax} color="#6b7280" />
              <Bar label="Posted" value={t.posted} max={funnelMax} color={INK} />
              <Bar label="Escalated" value={t.escalated} max={funnelMax} color="#0e7490" hint="handed to an agent" />
              <Bar label="Held back" value={t.held_back} max={funnelMax} color={WARN} hint="below the posting threshold" />
              <Bar label="Failed" value={t.failed} max={funnelMax} color={CRIT} />
            </section>

            {/* 7-day trend */}
            <Trend daily={d.week.daily || []} />
          </div>

          {/* Quality meters */}
          <section>
            <h2 className="text-sm font-semibold mb-3">This week - quality &amp; adoption</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Meter label="Avg confidence" value={d.week.avg_confidence} />
              <Meter label="Adoption" value={d.week.adoption_rate} />
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Feedback</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><ThumbsUp className="h-4 w-4" />{d.week.feedback_correct}</span>
                  <span className="inline-flex items-center gap-1 text-red-700 font-semibold"><ThumbsDown className="h-4 w-4" />{d.week.feedback_incorrect}</span>
                </div>
              </div>
              <div className="rounded-xl border border-border p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">Notes posted</div>
                <div className="text-2xl font-semibold tabular-nums">{d.week.posted}</div>
                <div className="text-xs text-muted-foreground">last 7 days</div>
              </div>
              {(() => {
                const p = d.week.providers || { ferry: 0, api: 0 };
                const tot = p.ferry + p.api;
                return (
                  <div className="rounded-xl border border-border p-4" title="Which gateway generated the drafts. Ferry rides the flat monthly pack; API is metered Anthropic usage.">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">Draft gateway</div>
                    {tot === 0 ? (
                      <div className="text-sm text-muted-foreground">—</div>
                    ) : (
                      <>
                        <div className="flex h-3 rounded overflow-hidden mb-1.5" style={{ gap: 2 }}>
                          {p.ferry > 0 && <div style={{ flex: p.ferry, background: '#7c3aed' }} />}
                          {p.api > 0 && <div style={{ flex: p.api, background: '#b45309' }} />}
                        </div>
                        <div className="flex justify-between text-[0.65rem]">
                          <span className="text-violet-700 font-medium">Ferry {p.ferry}</span>
                          <span className="text-amber-700 font-medium">API {p.api}{tot ? ` · ${Math.round((p.api / tot) * 100)}%` : ''}</span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            {/* adoption breakdown bar */}
            {adTotal > 0 && (
              <div className="mt-3 rounded-xl border border-border p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">How agents used our drafts</p>
                <div className="flex h-6 rounded-md overflow-hidden" style={{ gap: 2 }}>
                  {[['verbatim', ad.verbatim, INK], ['adapted', ad.adapted, '#0e7490'], ['ignored', ad.ignored, '#9ca3af']].map(([k, v, c]) =>
                    v > 0 ? <div key={k} style={{ flex: v, background: c }} title={`${k}: ${v}`} /> : null)}
                </div>
                <div className="flex gap-4 mt-2 text-[0.65rem] text-muted-foreground">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: INK }} />verbatim {ad.verbatim}</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: '#0e7490' }} />adapted {ad.adapted}</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm" style={{ background: '#9ca3af' }} />ignored {ad.ignored}</span>
                </div>
              </div>
            )}
          </section>

          {/* Category mix */}
          {d.week.by_category?.length > 0 && (
            <section className="rounded-xl border border-border p-4">
              <h2 className="text-sm font-semibold mb-3">Category mix (7 days)</h2>
              {(() => { const m = Math.max(...d.week.by_category.map((c) => c.n), 1);
                return d.week.by_category.map((c) => <Bar key={c.category} label={c.category} value={c.n} max={m} color={INK} />); })()}
            </section>
          )}

          {/* Recent activity */}
          <section>
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-sm font-semibold">Recent activity</h2>
              <span className="text-xs text-muted-foreground">what replai did, newest first</span>
            </div>
            <div className="rounded-xl border border-border divide-y divide-border overflow-hidden">
              {d.recent.map((r) => <ActivityRow key={r.run_id} r={r} />)}
            </div>
          </section>
        </div>
      )}
    </AppShell>
  );
}
